import { useState, useEffect, useCallback, useRef } from 'react';
import {
  IsoSimulationState,
  IsoAgent,
  IsoKPIDisplay,
  IsoOfficeLayout,
  DailyCheckpoint,
  CheckpointType,
  ISO_CONFIG,
  AGENT_STYLES,
  getTimeOfDay,
  getLightingForTime,
} from '../../../types/isometric.types';
import { getSocket } from '../../../lib/socket';

interface UseIsometricSimulationOptions {
  planId?: string;
  mundusId?: string;
  onMorningCheckpoint?: (checkpoint: DailyCheckpoint) => void;
  onEveningCheckpoint?: (checkpoint: DailyCheckpoint) => void;
}

// Default office layout
const createDefaultLayout = (): IsoOfficeLayout => ({
  width: ISO_CONFIG.GRID_COLS,
  height: ISO_CONFIG.GRID_ROWS,
  rooms: [
    {
      id: 'workspace',
      name: 'Main Workspace',
      type: 'WORKSPACE',
      bounds: { startCol: 2, startRow: 2, endCol: 10, endRow: 8 },
      floorType: 'FLOOR',
      furniture: [],
    },
    {
      id: 'meeting',
      name: 'Meeting Room',
      type: 'MEETING',
      bounds: { startCol: 12, startRow: 10, endCol: 18, endRow: 14 },
      floorType: 'CARPET',
      furniture: [],
    },
    {
      id: 'break',
      name: 'Break Area',
      type: 'BREAK',
      bounds: { startCol: 12, startRow: 2, endCol: 18, endRow: 6 },
      floorType: 'FLOOR',
      furniture: [],
    },
    {
      id: 'server',
      name: 'Server Room',
      type: 'SERVER',
      bounds: { startCol: 0, startRow: 12, endCol: 4, endRow: 15 },
      floorType: 'FLOOR',
      furniture: [],
    },
  ],
  tiles: [],
  furniture: [
    // Workspace desks
    { id: 'desk1', type: 'DESK_MODERN', position: { x: 3, y: 5 }, rotation: 0 },
    { id: 'desk2', type: 'DESK_MODERN', position: { x: 5, y: 5 }, rotation: 0 },
    { id: 'desk3', type: 'DESK_MODERN', position: { x: 7, y: 5 }, rotation: 0 },
    { id: 'desk4', type: 'DESK_MODERN', position: { x: 3, y: 8 }, rotation: 0 },
    { id: 'desk5', type: 'DESK_MODERN', position: { x: 5, y: 8 }, rotation: 0 },
    { id: 'desk6', type: 'DESK_MODERN', position: { x: 7, y: 8 }, rotation: 0 },
    // Chairs
    { id: 'chair1', type: 'CHAIR_OFFICE', position: { x: 3, y: 6 }, rotation: 0 },
    { id: 'chair2', type: 'CHAIR_OFFICE', position: { x: 5, y: 6 }, rotation: 0 },
    { id: 'chair3', type: 'CHAIR_OFFICE', position: { x: 7, y: 6 }, rotation: 0 },
    { id: 'chair4', type: 'CHAIR_OFFICE', position: { x: 3, y: 9 }, rotation: 0 },
    { id: 'chair5', type: 'CHAIR_OFFICE', position: { x: 5, y: 9 }, rotation: 0 },
    { id: 'chair6', type: 'CHAIR_OFFICE', position: { x: 7, y: 9 }, rotation: 0 },
    // Plants
    { id: 'plant1', type: 'PLANT_LARGE', position: { x: 1, y: 3 }, rotation: 0 },
    { id: 'plant2', type: 'PLANT_SMALL', position: { x: 9, y: 6 }, rotation: 0 },
    { id: 'plant3', type: 'PLANT_LARGE', position: { x: 18, y: 10 }, rotation: 0 },
    // Break area
    { id: 'coffee', type: 'COFFEE_MACHINE', position: { x: 16, y: 4 }, rotation: 0 },
    // Meeting room
    { id: 'meeting_table', type: 'TABLE_MEETING', position: { x: 15, y: 12 }, rotation: 0 },
    // THREE DISPLAY SCREENS - arranged across the top
    // LEFT: KPI Screen
    { id: 'kpi_screen', type: 'SCREEN_KPI', position: { x: 4, y: 1 }, rotation: 0 },
    // CENTER: Alarm Screen
    { id: 'alarm_screen', type: 'SCREEN_ALARM', position: { x: 10, y: 1 }, rotation: 0 },
    // RIGHT: Recommendations Screen
    { id: 'recommend_screen', type: 'SCREEN_RECOMMEND', position: { x: 16, y: 1 }, rotation: 0 },
  ],
  agentSpawnPoints: [
    { x: 0, y: 8 },
    { x: 0, y: 9 },
    { x: 0, y: 10 },
  ],
  kpiScreenPosition: { x: 10, y: 0 },
});

// Sample KPIs for testing - more comprehensive set
const createSampleKPIs = (): IsoKPIDisplay[] => [
  {
    id: 'kpi1',
    name: 'Pipeline Score',
    value: 78,
    target: 100,
    unit: 'pts',
    trend: 'UP',
    status: 'GOOD',
    alarmActive: false,
  },
  {
    id: 'kpi2',
    name: 'Skills Gap',
    value: 35,
    target: 20,
    unit: '%',
    trend: 'DOWN',
    status: 'WARNING',
    alarmActive: true,
  },
  {
    id: 'kpi3',
    name: 'Network Growth',
    value: 12,
    target: 10,
    unit: '/wk',
    trend: 'UP',
    status: 'EXCELLENT',
    alarmActive: false,
  },
  {
    id: 'kpi4',
    name: 'Applications Sent',
    value: 15,
    target: 25,
    unit: 'apps',
    trend: 'STABLE',
    status: 'WARNING',
    alarmActive: false,
  },
  {
    id: 'kpi5',
    name: 'Interview Rate',
    value: 8,
    target: 20,
    unit: '%',
    trend: 'DOWN',
    status: 'CRITICAL',
    alarmActive: true,
  },
];

export function useIsometricSimulation(options: UseIsometricSimulationOptions = {}) {
  const { onMorningCheckpoint, onEveningCheckpoint } = options;

  // Create initial agents - spawn them immediately so they're visible
  const createInitialAgents = (): IsoAgent[] => [
    {
      id: 'coordinator-1',
      name: 'Strategist',
      role: 'COORDINATOR',
      position: { x: 5, y: 5 },
      activity: 'WORKING',
      isBusy: true,
      activityProgress: 45,
      currentTask: 'Reviewing strategy',
      style: AGENT_STYLES.COORDINATOR,
    },
    {
      id: 'delegator-1',
      name: 'Analyst',
      role: 'DELEGATOR',
      position: { x: 4, y: 7 },
      activity: 'TYPING',
      isBusy: true,
      activityProgress: 30,
      currentTask: 'Analyzing data',
      style: AGENT_STYLES.DELEGATOR,
    },
    {
      id: 'worker-1',
      name: 'Scout',
      role: 'WORKER',
      position: { x: 7, y: 6 },
      activity: 'THINKING',
      isBusy: true,
      activityProgress: 60,
      currentTask: 'Processing tasks',
      style: AGENT_STYLES.WORKER,
    },
    {
      id: 'worker-2',
      name: 'Researcher',
      role: 'WORKER',
      position: { x: 3, y: 8 },
      activity: 'WORKING',
      isBusy: true,
      activityProgress: 25,
      currentTask: 'Market research',
      style: AGENT_STYLES.WORKER,
    },
    {
      id: 'delegator-2',
      name: 'Manager',
      role: 'DELEGATOR',
      position: { x: 15, y: 11 },
      activity: 'PRESENTING',
      isBusy: true,
      activityProgress: 75,
      currentTask: 'Team briefing',
      style: AGENT_STYLES.DELEGATOR,
    },
  ];

  // Simulation state - agents are spawned immediately
  const [state, setState] = useState<IsoSimulationState>(() => {
    const now = new Date();
    const lighting = getLightingForTime(now);
    const kpis = createSampleKPIs();
    // Initialize active alarms from KPIs that have alarmActive = true
    const activeAlarms = kpis.filter(k => k.alarmActive).map(k => k.id);
    return {
      isRunning: false,
      isPaused: false,
      speed: 1,
      currentTime: now,
      timeOfDay: getTimeOfDay(now.getHours()),
      lighting,
      layout: createDefaultLayout(),
      agents: createInitialAgents(), // Agents visible immediately!
      kpis,
      activeAlarms,
      showMorningModal: false,
      showEveningModal: false,
    };
  });

  // Checkpoint state
  const [currentCheckpoint, setCurrentCheckpoint] = useState<DailyCheckpoint | null>(null);
  const checkpointTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Time simulation interval
  const timeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Update time and lighting
  const updateSimulationTime = useCallback(() => {
    setState((prev) => {
      if (!prev.isRunning || prev.isPaused) return prev;

      // Advance time based on speed (1 real second = speed * simulated minutes)
      const newTime = new Date(prev.currentTime.getTime() + prev.speed * 60 * 1000);
      const newTimeOfDay = getTimeOfDay(newTime.getHours());
      const newLighting = getLightingForTime(newTime);

      return {
        ...prev,
        currentTime: newTime,
        timeOfDay: newTimeOfDay,
        lighting: newLighting,
      };
    });
  }, []);

  // Check for checkpoints (morning approval, evening debrief)
  const checkForCheckpoints = useCallback(() => {
    const hour = state.currentTime.getHours();

    // Morning checkpoint: 7-8 AM
    if (hour === 7 && !state.showMorningModal && !currentCheckpoint) {
      const checkpoint: DailyCheckpoint = {
        id: `morning-${Date.now()}`,
        type: 'MORNING_APPROVAL',
        scheduledTime: new Date(state.currentTime),
        expiresAt: new Date(state.currentTime.getTime() + 30 * 60 * 1000), // 30 min timeout
        status: 'PENDING',
      };

      setCurrentCheckpoint(checkpoint);
      setState((prev) => ({ ...prev, showMorningModal: true }));
      onMorningCheckpoint?.(checkpoint);

      // Auto-proceed after 30 minutes
      checkpointTimeoutRef.current = setTimeout(() => {
        handleAutoProceeded('MORNING_APPROVAL', 'No user response within 30 minutes');
      }, 30 * 60 * 1000 / state.speed); // Adjusted for simulation speed
    }

    // Evening checkpoint: 5-6 PM
    if (hour === 17 && !state.showEveningModal && !currentCheckpoint) {
      const checkpoint: DailyCheckpoint = {
        id: `evening-${Date.now()}`,
        type: 'EVENING_FEEDBACK',
        scheduledTime: new Date(state.currentTime),
        expiresAt: new Date(state.currentTime.getTime() + 60 * 60 * 1000), // 60 min timeout
        status: 'PENDING',
      };

      setCurrentCheckpoint(checkpoint);
      setState((prev) => ({ ...prev, showEveningModal: true }));
      onEveningCheckpoint?.(checkpoint);

      // Auto-proceed after 60 minutes
      checkpointTimeoutRef.current = setTimeout(() => {
        handleAutoProceeded('EVENING_FEEDBACK', 'No user response within 60 minutes');
      }, 60 * 60 * 1000 / state.speed); // Adjusted for simulation speed
    }
  }, [state.currentTime, state.showMorningModal, state.showEveningModal, currentCheckpoint, state.speed, onMorningCheckpoint, onEveningCheckpoint]);

  // Handle auto-proceed when user doesn't respond
  const handleAutoProceeded = useCallback((type: CheckpointType, reason: string) => {
    setCurrentCheckpoint((prev) =>
      prev
        ? {
            ...prev,
            status: 'AUTO_PROCEEDED',
            autoProceededAt: new Date(),
            autoProceededReason: reason,
          }
        : null
    );

    setState((prev) => ({
      ...prev,
      showMorningModal: type === 'MORNING_APPROVAL' ? false : prev.showMorningModal,
      showEveningModal: type === 'EVENING_FEEDBACK' ? false : prev.showEveningModal,
    }));

    // Log auto-proceed event
    console.log(`[Simulation] Auto-proceeded ${type}: ${reason}`);
  }, []);

  // Handle user response to checkpoint
  const respondToCheckpoint = useCallback(
    (response: DailyCheckpoint['response']) => {
      if (checkpointTimeoutRef.current) {
        clearTimeout(checkpointTimeoutRef.current);
      }

      setCurrentCheckpoint((prev) =>
        prev
          ? {
              ...prev,
              status: 'RESPONDED',
              response,
            }
          : null
      );

      setState((prev) => ({
        ...prev,
        showMorningModal: false,
        showEveningModal: false,
      }));
    },
    []
  );

  // Start simulation
  const start = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isRunning: true,
      isPaused: false,
    }));

    // Spawn agents if not already present - position them visibly in the workspace
    setState((prev) => {
      if (prev.agents.length === 0) {
        const newAgents: IsoAgent[] = [
          {
            id: 'coordinator-1',
            name: 'Strategist',
            role: 'COORDINATOR',
            position: { x: 5, y: 5 }, // Center of workspace
            targetPosition: { x: 7, y: 3 }, // Start walking to a desk
            activity: 'WALKING',
            isBusy: false,
            currentTask: 'Heading to desk',
            style: AGENT_STYLES.COORDINATOR,
          },
          {
            id: 'delegator-1',
            name: 'Analyst',
            role: 'DELEGATOR',
            position: { x: 4, y: 4 }, // Near desks
            targetPosition: { x: 5, y: 6 }, // Walk to another desk
            activity: 'WALKING',
            isBusy: false,
            currentTask: 'Going to workstation',
            style: AGENT_STYLES.DELEGATOR,
          },
          {
            id: 'worker-1',
            name: 'Scout',
            role: 'WORKER',
            position: { x: 6, y: 5 }, // In workspace area
            targetPosition: { x: 14, y: 3 }, // Head to coffee machine
            activity: 'WALKING',
            isBusy: false,
            currentTask: 'Getting coffee',
            style: AGENT_STYLES.WORKER,
          },
          {
            id: 'worker-2',
            name: 'Researcher',
            role: 'WORKER',
            position: { x: 3, y: 6 }, // At a desk
            activity: 'TYPING',
            isBusy: true,
            activityProgress: 25,
            currentTask: 'Analyzing market data',
            style: AGENT_STYLES.WORKER,
          },
          {
            id: 'delegator-2',
            name: 'Coordinator',
            role: 'DELEGATOR',
            position: { x: 15, y: 12 }, // Meeting room
            activity: 'PRESENTING',
            isBusy: true,
            activityProgress: 60,
            currentTask: 'Strategy presentation',
            style: AGENT_STYLES.DELEGATOR,
          },
        ];
        return { ...prev, agents: newAgents };
      }
      return prev;
    });
  }, []);

  // Pause simulation
  const pause = useCallback(() => {
    setState((prev) => ({ ...prev, isPaused: true }));
  }, []);

  // Resume simulation
  const resume = useCallback(() => {
    setState((prev) => ({ ...prev, isPaused: false }));
  }, []);

  // Stop simulation
  const stop = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isRunning: false,
      isPaused: false,
    }));

    if (checkpointTimeoutRef.current) {
      clearTimeout(checkpointTimeoutRef.current);
    }
  }, []);

  // Set simulation speed
  const setSpeed = useCallback((speed: 1 | 2 | 4 | 8) => {
    setState((prev) => ({ ...prev, speed }));
  }, []);

  // Move agent to position
  const moveAgent = useCallback((agentId: string, targetPosition: { x: number; y: number }) => {
    setState((prev) => ({
      ...prev,
      agents: prev.agents.map((agent) =>
        agent.id === agentId
          ? {
              ...agent,
              targetPosition,
              activity: 'WALKING',
            }
          : agent
      ),
    }));
  }, []);

  // Set agent activity
  const setAgentActivity = useCallback(
    (agentId: string, activity: IsoAgent['activity'], task?: string) => {
      setState((prev) => ({
        ...prev,
        agents: prev.agents.map((agent) =>
          agent.id === agentId
            ? {
                ...agent,
                activity,
                currentTask: task,
                isBusy: activity !== 'IDLE',
              }
            : agent
        ),
      }));
    },
    []
  );

  // Update KPI value
  const updateKPI = useCallback(
    (kpiId: string, value: number, trend?: 'UP' | 'DOWN' | 'STABLE') => {
      setState((prev) => ({
        ...prev,
        kpis: prev.kpis.map((kpi) =>
          kpi.id === kpiId
            ? {
                ...kpi,
                value,
                trend: trend || kpi.trend,
                status:
                  value >= kpi.target * 0.9
                    ? 'EXCELLENT'
                    : value >= kpi.target * 0.7
                      ? 'GOOD'
                      : value >= kpi.target * 0.5
                        ? 'WARNING'
                        : 'CRITICAL',
              }
            : kpi
        ),
      }));
    },
    []
  );

  // Trigger alarm
  const triggerAlarm = useCallback((kpiId: string) => {
    setState((prev) => ({
      ...prev,
      kpis: prev.kpis.map((kpi) =>
        kpi.id === kpiId ? { ...kpi, alarmActive: true } : kpi
      ),
      activeAlarms: [...prev.activeAlarms, kpiId],
    }));
  }, []);

  // Clear alarm
  const clearAlarm = useCallback((kpiId: string) => {
    setState((prev) => ({
      ...prev,
      kpis: prev.kpis.map((kpi) =>
        kpi.id === kpiId ? { ...kpi, alarmActive: false } : kpi
      ),
      activeAlarms: prev.activeAlarms.filter((id) => id !== kpiId),
    }));
  }, []);

  // Agent movement and activity simulation - ALWAYS runs for visual effect
  // Agents move autonomously to give the sense of a working office
  useEffect(() => {
    // Define key locations in the office for agent movement (inside useEffect)
    const officeLocations = [
      { name: 'desk1', x: 3, y: 5 },
      { name: 'desk2', x: 5, y: 5 },
      { name: 'desk3', x: 7, y: 5 },
      { name: 'desk4', x: 3, y: 8 },
      { name: 'desk5', x: 5, y: 8 },
      { name: 'desk6', x: 7, y: 8 },
      { name: 'coffee_area', x: 16, y: 4 },
      { name: 'meeting_room', x: 15, y: 12 },
      { name: 'kpi_screen', x: 4, y: 2 },
      { name: 'alarm_screen', x: 10, y: 2 },
      { name: 'recommend_screen', x: 16, y: 2 },
      { name: 'server_room', x: 2, y: 13 },
      { name: 'plant_corner', x: 18, y: 10 },
      { name: 'entrance', x: 1, y: 10 },
      { name: 'center', x: 6, y: 7 },
      { name: 'break_area', x: 14, y: 5 },
    ];

    const interval = setInterval(() => {
      setState((prev) => {
        // Calculate effective speed inside setState to use latest state
        const isActiveSimulation = prev.isRunning && !prev.isPaused;
        const effectiveSpeed = isActiveSimulation ? prev.speed : 0.5; // Slower demo speed

        const updatedAgents = prev.agents.map((agent) => {
          // Handle walking to target
          if (agent.targetPosition && agent.activity === 'WALKING') {
            const dx = agent.targetPosition.x - agent.position.x;
            const dy = agent.targetPosition.y - agent.position.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < 0.3) {
              // Arrived at destination - start an activity
              const activities: IsoAgent['activity'][] = [
                'WORKING',
                'TYPING',
                'THINKING',
                'DRINKING_COFFEE',
                'PRESENTING',
              ];
              return {
                ...agent,
                position: agent.targetPosition,
                targetPosition: undefined,
                activity: activities[Math.floor(Math.random() * activities.length)],
                activityProgress: 0,
              };
            }

            // Move towards target with smooth animation
            const moveSpeed = 0.15 * effectiveSpeed;
            return {
              ...agent,
              position: {
                x: agent.position.x + (dx / distance) * moveSpeed,
                y: agent.position.y + (dy / distance) * moveSpeed,
              },
            };
          }

          // AUTONOMOUS MOVEMENT: When idle for a bit, agents pick a new destination
          if (agent.activity === 'IDLE') {
            // 5% chance per tick to start moving somewhere
            if (Math.random() < 0.05) {
              const destination = officeLocations[Math.floor(Math.random() * officeLocations.length)];
              return {
                ...agent,
                targetPosition: { x: destination.x, y: destination.y },
                activity: 'WALKING' as const,
                currentTask: `Going to ${destination.name.replace('_', ' ')}`,
              };
            }
            // 3% chance to start working in place
            if (Math.random() < 0.03) {
              const activities: IsoAgent['activity'][] = [
                'WORKING',
                'TYPING',
                'THINKING',
              ];
              return {
                ...agent,
                activity: activities[Math.floor(Math.random() * activities.length)],
                activityProgress: 0,
                currentTask: agent.role === 'COORDINATOR'
                  ? 'Reviewing strategy'
                  : agent.role === 'DELEGATOR'
                    ? 'Analyzing data'
                    : 'Processing tasks',
              };
            }
          }

          // Progress active activities - they complete and agent becomes idle briefly before moving
          if (agent.activity !== 'IDLE' && agent.activity !== 'WALKING') {
            const progressRate = (1.5 + Math.random() * 1) * effectiveSpeed;
            const newProgress = (agent.activityProgress || 0) + progressRate;

            if (newProgress >= 100) {
              // Activity complete - high chance to immediately pick new destination
              if (Math.random() < 0.7) {
                const destination = officeLocations[Math.floor(Math.random() * officeLocations.length)];
                return {
                  ...agent,
                  targetPosition: { x: destination.x, y: destination.y },
                  activity: 'WALKING' as const,
                  activityProgress: 0,
                  currentTask: `Heading to ${destination.name.replace('_', ' ')}`,
                };
              }
              return {
                ...agent,
                activity: 'IDLE' as const,
                activityProgress: 0,
                currentTask: undefined,
              };
            }
            return {
              ...agent,
              activityProgress: newProgress,
            };
          }

          return agent;
        });

        return { ...prev, agents: updatedAgents };
      });
    }, 100); // Fixed tick rate - speed is handled inside setState

    return () => clearInterval(interval);
  }, []); // No dependencies - runs once on mount, uses functional setState

  // Time simulation
  useEffect(() => {
    if (!state.isRunning || state.isPaused) {
      if (timeIntervalRef.current) {
        clearInterval(timeIntervalRef.current);
      }
      return;
    }

    timeIntervalRef.current = setInterval(() => {
      updateSimulationTime();
      checkForCheckpoints();
    }, 1000);

    return () => {
      if (timeIntervalRef.current) {
        clearInterval(timeIntervalRef.current);
      }
    };
  }, [state.isRunning, state.isPaused, updateSimulationTime, checkForCheckpoints]);

  // Socket event handlers
  useEffect(() => {
    const socket = getSocket();
    if (!socket || !socket.connected) return;

    const handleStateUpdate = (update: { type: string; agentId?: string; position?: { x: number; y: number }; kpiId?: string; value?: number; trend?: 'UP' | 'DOWN' | 'STABLE' }) => {
      // Handle real-time updates from backend
      if (update.type === 'AGENT_MOVED' && update.agentId && update.position) {
        moveAgent(update.agentId, update.position);
      }
      if (update.type === 'KPI_UPDATE' && update.kpiId && update.value !== undefined) {
        updateKPI(update.kpiId, update.value, update.trend);
      }
      if (update.type === 'ALARM_TRIGGERED' && update.kpiId) {
        triggerAlarm(update.kpiId);
      }
    };

    socket.on('simulation:update', handleStateUpdate);

    return () => {
      socket.off('simulation:update', handleStateUpdate);
    };
  }, [moveAgent, updateKPI, triggerAlarm]);

  return {
    state,
    currentCheckpoint,
    actions: {
      start,
      pause,
      resume,
      stop,
      setSpeed,
      moveAgent,
      setAgentActivity,
      updateKPI,
      triggerAlarm,
      clearAlarm,
      respondToCheckpoint,
    },
  };
}
